import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const QUESTION = "How do I start investing with no money?"

const SOURCES = [
  '📗 Rich Dad Poor Dad — Robert Kiyosaki',
  '📗 The Millionaire Next Door',
  '📗 I Will Teach You To Be Rich — Ramit Sethi',
]

const ANSWER_LINES = [
  "To invest with no money, start with these principles:",
  "1. Take Action and Learn from Others — Kiyosaki emphasizes finding someone who has done what you want and learning directly from them.",
  "2. Make Offers — Make multiple offers on properties even below asking price. Most investors don't because they fear rejection.",
  "3. Understand the Financials — Ramit Sethi advises a 20% down payment rule and keeping monthly payments under 30% of gross income.",
  "4. Leverage Other People's Money — Use bank financing to acquire assets while your own capital works elsewhere."
]
const ANSWER_WORDS = ANSWER_LINES.join(' ').split(' ')
const ANSWER_LINE_LENGTHS = ANSWER_LINES.map(l => l.split(' ').length)

export default function ChatDemo() {
  const [demoState, setDemoState] = useState('IDLE')
  const [typedText, setTypedText] = useState('')
  const [visibleSources, setVisibleSources] = useState([])
  const [streamedWords, setStreamedWords] = useState([])

  // IDLE → TYPING
  useEffect(() => {
    if (demoState !== 'IDLE') return
    const id = setTimeout(() => setDemoState('TYPING'), 1000)
    return () => clearTimeout(id)
  }, [demoState])

  // TYPING → SENT
  useEffect(() => {
    if (demoState !== 'TYPING') return
    const id = setInterval(() => {
      setTypedText(prev => {
        const next = prev + QUESTION[prev.length]
        if (next.length === QUESTION.length) {
          clearInterval(id)
          setDemoState('SENT')
        }
        return next
      })
    }, 40)
    return () => clearInterval(id)
  }, [demoState])

  // SENT → SOURCES_APPEAR
  useEffect(() => {
    if (demoState !== 'SENT') return
    setTypedText('')
    const id = setTimeout(() => setDemoState('SOURCES_APPEAR'), 500)
    return () => clearTimeout(id)
  }, [demoState])

  // SOURCES_APPEAR → STREAMING
  useEffect(() => {
    if (demoState !== 'SOURCES_APPEAR') return
    const timers = SOURCES.map((_, i) =>
      setTimeout(() => setVisibleSources(prev => [...prev, i]), i * 500)
    )
    const next = setTimeout(() => setDemoState('STREAMING'), 2000)
    return () => {
      timers.forEach(clearTimeout)
      clearTimeout(next)
    }
  }, [demoState])

  // STREAMING → RESET
  useEffect(() => {
    if (demoState !== 'STREAMING') return
    const id = setInterval(() => {
      setStreamedWords(prev => {
        if (prev.length >= ANSWER_WORDS.length) {
          clearInterval(id)
          return prev
        }
        return [...prev, ANSWER_WORDS[prev.length]]
      })
    }, 80)
    const timer = setTimeout(() => setDemoState('RESET'), 4500)
    return () => {
      clearInterval(id)
      clearTimeout(timer)
    }
  }, [demoState])

  // RESET → IDLE
  useEffect(() => {
    if (demoState !== 'RESET') return
    setTypedText('')
    setVisibleSources([])
    setStreamedWords([])
    setDemoState('IDLE')
  }, [demoState])

  const showMessage = !['IDLE', 'TYPING'].includes(demoState)
  const showAnswer = !['IDLE', 'TYPING', 'SENT'].includes(demoState)

  return (
    <div className="chat-demo">
      <div className="chat-demo-browser">
        <div className="chat-demo-browser-bar">
          <div className="chat-demo-dots">
            <span className="chat-demo-dot chat-demo-dot--red" />
            <span className="chat-demo-dot chat-demo-dot--yellow" />
            <span className="chat-demo-dot chat-demo-dot--green" />
          </div>
          <div className="chat-demo-url">growlab.cloud</div>
        </div>
        <div className="chat-demo-content">
          <div className="chat-demo-messages">
            <AnimatePresence>
              {showMessage && (
                <motion.div
                  key="user-msg"
                  className="chat-demo-message"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {QUESTION}
                </motion.div>
              )}
            </AnimatePresence>

            {showAnswer && (
              <div className="chat-demo-answer">
                <div className="chat-demo-sources">
                  <AnimatePresence>
                    {visibleSources.map(idx => (
                      <motion.div
                        key={idx}
                        className="chat-demo-pill"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {SOURCES[idx]}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <div className="chat-demo-text">
                  {ANSWER_LINE_LENGTHS.map((len, lineIdx) => {
                    const start = ANSWER_LINE_LENGTHS.slice(0, lineIdx).reduce((a, b) => a + b, 0)
                    const words = streamedWords.slice(start, start + len)
                    if (words.length === 0) return null
                    return <div key={lineIdx} className="chat-demo-text-line">{words.join(' ')}</div>
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="chat-demo-input-row">
            <div className="chat-demo-input">
              {typedText}
              {(demoState === 'IDLE' || demoState === 'TYPING') && (
                <span className="chat-demo-cursor" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
